import type { CityConfig } from "../city-template/types";

export const bardstownConfig: CityConfig = {
  city: "Bardstown",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "bardstown",
  route: "/cities/bardstown",
  title: "Emergency Passport Bardstown, KY | Fast Passport Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Bardstown, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Nelson County and Bourbon Trail travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Bardstown, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Bardstown and Nelson County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Bardstown%20Kentucky%20historic%20downtown%20at%20golden%20hour%20with%20charming%20Federal-style%20architecture%20and%20historic%20courthouse%2C%20warm%20Nelson%20County%20sunset%20light%2C%20Bourbon%20Trail%20heritage%20district%2C%20small%20town%20America%20charm%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Central%20Kentucky%20scene%20with%20rolling%20green%20countryside&width=1800&height=700&seq=bardstown-hero-001&orientation=landscape",
  heroImageAlt: "Bardstown Kentucky historic downtown at golden hour with Federal-style architecture and Nelson County landscape",
  geoPosition: "37.8092;-85.4669",
  geoPlacename: "Bardstown, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "77 W Jackson Blvd, Suite 1200, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 370 miles from Bardstown via I-65",
  },
  nearbyCitiesZips: [
    { name: "Bardstown", zips: "40004" },
    { name: "Springfield", zips: "40069" },
    { name: "Taylorsville", zips: "40071" },
    { name: "Lebanon", zips: "40033" },
    { name: "New Haven", zips: "40051" },
    { name: "Hodgenville", zips: "42748" },
    { name: "Shepherdsville", zips: "40165" },
    { name: "Elizabethtown", zips: "42701, 42702" },
    { name: "Louisville", zips: "40201–40299" },
    { name: "Radcliff", zips: "40160" },
    { name: "Mount Washington", zips: "40047" },
    { name: "Frankfort", zips: "40601, 40602, 40604, 40618, 40619, 40620, 40621, 40622" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Nelson County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Louisville", route: "/cities/louisville" },
    { name: "Fast passport renewal in Lexington", route: "/cities/lexington" },
    { name: "Emergency passport help in Bowling Green", route: "/cities/bowling-green" },
    { name: "Urgent passport services in Elizabethtown", route: "/cities/elizabethtown" },
    { name: "Same-day passport help in Frankfort", route: "/cities/frankfort" },
    { name: "Passport courier services near Shepherdsville", route: "/shepherdsville" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Bardstown remotely via secure courier. We do not have a walk-in office in Bardstown — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Nelson County travelers.",
  fpcOfficeDistance: "Serving all of Nelson County and the Bourbon Trail region",
  serviceAreaIntro: "From historic downtown Bardstown to the surrounding Bourbon Trail region, from Taylorsville to Springfield — Fast Passport Center serves the entire Bardstown community and surrounding Nelson County. We regularly help travelers from Lebanon, New Haven, and Hodgenville get their passports fast.",
  nearbyCitiesIntro: "We serve all Bardstown neighborhoods and nearby Central Kentucky cities including Springfield, Taylorsville, Lebanon, New Haven, Hodgenville, Shepherdsville, Elizabethtown, Louisville, Radcliff, Mount Washington, and Frankfort. If you are in Nelson County, central Kentucky, or the Bourbon Trail region, we have you covered.",
  howItWorksIntro: "Most Bardstown travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Bardstown travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Bardstown and Nelson County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Bardstown? Here are the most common ones we hear from Nelson County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Nelson County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Thomas K.",
      location: "Bardstown, KY",
      rating: 5,
      text: "My passport expired before my Bourbon Trail tour of Scotland. Fast Passport Center got me a renewal in 3 days. I made my distillery tour in the Highlands.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah J.",
      location: "Lebanon, KY",
      rating: 5,
      text: "Needed an emergency passport for a family emergency in Germany. They were compassionate, professional, and got my passport in 36 hours. Forever grateful.",
      service: "Emergency Passport",
    },
    {
      name: "David L.",
      location: "Springfield, KY",
      rating: 5,
      text: "First-time passport for my honeymoon in Jamaica. They guided me through the entire process and I had my passport in 5 days. Couldn't have done it without them.",
      service: "New Passport",
    },
    {
      name: "Michelle P.",
      location: "Taylorsville, KY",
      rating: 5,
      text: "Lost my passport the day before my flight to Cancun. Panicked doesn't begin to describe it. They got me a replacement in 24 hours. Absolute lifesavers.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "bardstown-1",
      name: "Bardstown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "104 W Flaget Ave",
      city: "Bardstown",
      state: "KY",
      zip: "40004",
      phone: "(502) 348-3711",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Bardstown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Nelson County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "bardstown-2",
      name: "Lebanon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "226 N Spalding Ave",
      city: "Lebanon",
      state: "KY",
      zip: "40033",
      phone: "(270) 692-3121",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Lebanon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves eastern Nelson County and Marion County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "bardstown-3",
      name: "Elizabethtown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 W Dixie Ave",
      city: "Elizabethtown",
      state: "KY",
      zip: "42701",
      phone: "(270) 765-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Elizabethtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Hardin County travelers. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "bardstown-4",
      name: "Shepherdsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1361 N Buckman St",
      city: "Shepherdsville",
      state: "KY",
      zip: "40165",
      phone: "(502) 543-2811",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Shepherdsville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Bullitt County and the I-65 corridor. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "bardstown-5",
      name: "Louisville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1417 Gardiner Ln",
      city: "Louisville",
      state: "KY",
      zip: "40213",
      phone: "(502) 637-5511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Louisville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Jefferson County. High volume — book appointments 2+ weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "bardstown-6",
      name: "Nelson County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "113 E Flaget Ave",
      city: "Bardstown",
      state: "KY",
      zip: "40004",
      phone: "(502) 348-1830",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Bardstown Courthouse",
      website: "https://www.nelsoncountyky.gov/",
      websiteLabel: "Nelson County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 6 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Nelson County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Nelson County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Bardstown applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Nelson County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Nelson County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Bardstown." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Bardstown address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Bardstown before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Bardstown for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 6 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Bardstown?", a: "Fast Passport Center offers fast passport renewal in Bardstown in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Bardstown in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Bardstown?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — a 6 hour drive from Bardstown — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Nelson County residents." },
    { q: "Can I expedite a child passport in Bardstown?", a: "Yes. Fast Passport Center expedites child passports in Bardstown for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Nelson County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Bardstown with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Bardstown?", a: "With Fast Passport Center's emergency courier service, Bardstown travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Bardstown?", a: "Popular options include the Bardstown Post Office (40004), Lebanon Post Office (40033), Elizabethtown Post Office (42701), Shepherdsville Post Office (40165), Louisville Main Post Office (40213), and Nelson County Clerk (40004). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Bardstown?", a: "Same-day passports are only available at the Chicago Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 6 hour drive to Chicago." },
    { q: "What if I live in a smaller Nelson County town?", a: "We serve the entire Nelson County and surrounding area including Springfield, Taylorsville, Lebanon, New Haven, Hodgenville, Shepherdsville, Elizabethtown, Louisville, Radcliff, Mount Washington, and Frankfort. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Bardstown?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Bardstown?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Bardstown, Lebanon, and Elizabethtown offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Bardstown?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Bardstown?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Nelson County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Bardstown", address: "3815 E John Rowan Blvd, Bardstown, KY 40004", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Bardstown", address: "111 W John Rowan Blvd, Bardstown, KY 40004", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Bardstown", address: "3795 E John Rowan Blvd, Bardstown, KY 40004", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Bardstown", address: "106 E Flaget Ave, Bardstown, KY 40004", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Elizabethtown", address: "1604 N Dixie Hwy, Elizabethtown, KY 42701", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Elizabethtown", address: "100 Walmart Dr, Elizabethtown, KY 42701", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Shepherdsville", address: "545 Conestoga Pkwy, Shepherdsville, KY 40165", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Louisville", address: "4600 Outer Loop, Louisville, KY 40219", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Nelson County",
      address: "113 E Flaget Ave, Bardstown, KY 40004",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Hardin County",
      address: "100 Public Sq, Elizabethtown, KY 42701",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Marion County",
      address: "102 W Main St, Lebanon, KY 40033",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Marion County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Bullitt County",
      address: "300 S Buckman St, Shepherdsville, KY 40165",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Bullitt County residents. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "45 miles from Bardstown",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "The primary airport for Nelson County. Domestic flights with connections to major international hubs. Accessible via I-65.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: true,
    },
    {
      code: "LEX",
      name: "Blue Grass Airport (Lexington)",
      distance: "65 miles from Bardstown",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "Alternative regional airport with flights to major hubs.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Blue Grass Airport",
      featured: false,
    },
    {
      code: "BNA",
      name: "Nashville International Airport",
      distance: "110 miles from Bardstown",
      address: "1 Terminal Dr, Nashville, TN 37214",
      note: "Major international hub for long-haul flights. Accessible via I-65.",
      flightUrl: "https://www.flynashville.com/",
      flightLabel: "BNA Airport",
      featured: false,
    },
  ],
};